import { useEffect, useRef, useCallback } from 'react';
import { INFINITE_SCROLL_ROOT_MARGIN } from '../constants/eventGrid';

/**
 * Fires onLoadMore when the sentinel enters the expanded viewport (early preload).
 */
export function useInfiniteScrollTrigger({
  onLoadMore,
  hasMore,
  loading,
  rootMargin = INFINITE_SCROLL_ROOT_MARGIN
}) {
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const loadingRef = useRef(loading);

  onLoadMoreRef.current = onLoadMore;
  loadingRef.current = loading;

  const stableLoadMore = useCallback(() => {
    if (!loadingRef.current) {
      onLoadMoreRef.current();
    }
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          stableLoadMore();
        }
      },
      { rootMargin, threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, stableLoadMore, rootMargin]);

  return sentinelRef;
}
